import React, { memo } from 'react';
import { DateTime, Interval } from 'luxon';

import {
	TypographyComponent,
	TypographySize,
	TypographyVariant,
	TypographyWeight
} from '@vaa-component-lib/component.atom.typography';

import DayButtonComponent, { SelectionType } from '../day/day.component';

import { PRODUCT_TYPES } from '../../types/criteria';

import {
	DateGrid,
	DatePicker,
	DaysHeading,
	MonthHeading
} from './month-view.styles';

interface MonthViewComponentProps {
	id?: string;
	startDate?: string;
	selectedDateRange?: string[];
	showDays?: boolean;
	onDaySelected: (dates: string) => void;
	isVisible: boolean;
	enabledDateRange?: string[];
	bookingType?: string;
}

const MonthViewComponent: React.FC<MonthViewComponentProps> = ({
	id,
	startDate,
	selectedDateRange,
	enabledDateRange = [],
	showDays = false,
	onDaySelected,
	bookingType,
	isVisible
}) => {
	const localStartDate = DateTime.fromISO(startDate).startOf('month');
	const localEndDate = DateTime.fromISO(startDate).endOf('month');
	const interval = Interval.fromDateTimes(localStartDate, localEndDate);
	const dates = interval.splitBy({ days: 1 }).map((d) => d);
	const todayDate = DateTime.now();
	const yesterday = todayDate.minus({ days: 1 });
	const isFlightOnly = bookingType === PRODUCT_TYPES.FLIGHT_ONLY;

	return (
		<DatePicker data-cy={'month-view-component'} id={id} role="listbox">
			<MonthHeading>
				<TypographyComponent
					element="p"
					variant={TypographyVariant.Heading}
					size={TypographySize.Xxs}
					weight={TypographyWeight.Medium}>
					{localStartDate?.monthLong} {localStartDate.year}
				</TypographyComponent>
			</MonthHeading>
			{showDays && (
				<DaysHeading data-cy={'month-view-day-component'}>
					<TypographyComponent
						element="div"
						variant={TypographyVariant.Body}
						size={TypographySize.Xsm}
						weight={TypographyWeight.Regular}>
						<div>Mon</div>
						<div>Tue</div>
						<div>Wed</div>
						<div>Thu</div>
						<div>Fri</div>
						<div>Sat</div>
						<div>Sun</div>
					</TypographyComponent>
				</DaysHeading>
			)}
			<TypographyComponent
				element="div"
				variant={TypographyVariant.Body}
				size={TypographySize.Sml}
				weight={TypographyWeight.Regular}>
				<DateGrid offset={localStartDate.toFormat('c')}>
					{dates?.map((date: Interval, i: number) => {
						const ranges = selectedDateRange?.map((date) =>
							DateTime.fromISO(date)
						);
						const isoDate = date.start.toISO();
						const selected =
							ranges.some(
								(range) => range.toMillis() === date.start.toMillis()
							) ||
							(ranges.length === 2 &&
								date.start.toMillis() > ranges[0].toMillis() &&
								date.start.toMillis() < ranges[1].toMillis());

						let selectionType = SelectionType.RangeMiddle;
						if (
							ranges.length === 2 &&
							ranges[0].toMillis() === date.start.toMillis()
						)
							selectionType = SelectionType.RangeStart;
						if (
							ranges.length === 2 &&
							ranges[1].toMillis() === date.start.toMillis()
						)
							selectionType = SelectionType.RangeEnd;
						if (
							ranges.length === 1 &&
							ranges[0].toMillis() === date.start.toMillis()
						)
							selectionType = SelectionType.Single;

						const enabledRanges = enabledDateRange?.map((date) =>
							DateTime.fromISO(date)
						);
						const isWithinEnabledRange =
							enabledDateRange.length &&
							(date.start >= enabledRanges[0] || !enabledRanges[0]?.isValid) &&
							(date.start <= enabledRanges[1] || !enabledRanges[1]?.isValid);

						const isAfter330Days =
							isFlightOnly && date.start > todayDate.plus({ days: 330 });

						return (
							<DayButtonComponent
								selectionType={selectionType}
								selected={selected}
								disabled={
									date.start < yesterday ||
									(enabledDateRange.length > 1 && !isWithinEnabledRange) ||
									isAfter330Days
								}
								date={isoDate}
								key={isoDate}
								isToday={date.start.hasSame(todayDate, 'day')}
								onClick={() => onDaySelected(isoDate)}
								tabIndex={isVisible ? 0 : -1}
							/>
						);
					})}
				</DateGrid>
			</TypographyComponent>
		</DatePicker>
	);
};

export default memo(MonthViewComponent);
